import React, { useState, useEffect } from 'react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Receipt, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle,
  Clock,
  DollarSign,
  Search,
  Filter,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';

const EXPENSE_CATEGORIES = [
  { value: 'travel', label: 'Travel' },
  { value: 'filing_fees', label: 'Filing Fees' },
  { value: 'expert_witness', label: 'Expert Witness' },
  { value: 'postage', label: 'Postage & Delivery' },
  { value: 'copies', label: 'Copies & Printing' },
  { value: 'meals', label: 'Meals & Entertainment' },
  { value: 'lodging', label: 'Lodging' },
  { value: 'other', label: 'Other' },
];

export const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatus, setActiveStatus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'other',
    case_id: '',
    client_id: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    billable: true,
    reimbursable: false,
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, summaryRes, casesRes, clientsRes] = await Promise.all([
        api.get('/expenses'),
        api.get('/expenses/summary'),
        api.get('/cases'),
        api.get('/clients'),
      ]);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
      setCases(casesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString(),
      };
      if (!data.case_id || data.case_id === 'none') delete data.case_id;
      if (!data.client_id || data.client_id === 'none') delete data.client_id;
      
      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.expense_id}`, data);
        toast.success('Expense updated');
      } else {
        await api.post('/expenses', data);
        toast.success('Expense created');
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error('Failed to save expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      description: expense.description,
      amount: expense.amount.toString(),
      category: expense.category,
      case_id: expense.case_id || '',
      client_id: expense.client_id || '',
      date: expense.date?.split('T')[0] || new Date().toISOString().split('T')[0],
      vendor: expense.vendor || '',
      billable: expense.billable,
      reimbursable: expense.reimbursable,
      notes: expense.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/expenses/${deleteId}`);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    } finally {
      setDeleteId(null);
    }
  };

  const handleStatusChange = async (expenseId, newStatus) => {
    try {
      await api.put(`/expenses/${expenseId}/status`, { status: newStatus });
      toast.success(`Expense ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      category: 'other',
      case_id: '',
      client_id: '',
      date: new Date().toISOString().split('T')[0],
      vendor: '',
      billable: true,
      reimbursable: false,
      notes: '',
    });
    setEditingExpense(null);
    setIsDialogOpen(false);
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(e => {
    const matchesSearch = e.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (e.vendor || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = activeStatus === 'all' || e.status === activeStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      reimbursed: 'bg-blue-100 text-blue-700',
      billed: 'bg-purple-100 text-purple-700',
    };
    return colors[status] || colors.pending;
  };

  const getCategoryLabel = (category) => {
    return EXPENSE_CATEGORIES.find(c => c.value === category)?.label || category;
  };

  if (loading) {
    return <PageLoader text="Loading expenses..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Expense Tracking</h1>
          <p className="text-slate-500 mt-1">Track and manage case-related expenses</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-expense-btn">
              <Plus className="h-4 w-4" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    placeholder="Court filing fee"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor">Vendor</Label>
                  <Input
                    id="vendor"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="Vendor name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="case_id">Related Case</Label>
                  <Select value={formData.case_id} onValueChange={(v) => {
                    // Auto-select client when case is selected
                    const selectedCase = cases.find(c => c.case_id === v);
                    setFormData({ 
                      ...formData, 
                      case_id: v,
                      client_id: selectedCase?.client_id || formData.client_id 
                    });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select case" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {cases.map(c => (
                        <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_id">Client {formData.case_id && formData.case_id !== 'none' && <span className="text-xs text-slate-500">(auto-selected from case)</span>}</Label>
                  <Select 
                    value={formData.client_id} 
                    onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                    disabled={formData.case_id && formData.case_id !== 'none'}
                  >
                    <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {(formData.case_id && formData.case_id !== 'none' 
                        ? clients.filter(c => c.client_id === cases.find(cs => cs.case_id === formData.case_id)?.client_id)
                        : clients
                      ).map(c => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="flex items-center gap-6 col-span-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="billable"
                      checked={formData.billable}
                      onCheckedChange={(c) => setFormData({ ...formData, billable: c })}
                    />
                    <Label htmlFor="billable">Billable to client</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="reimbursable"
                      checked={formData.reimbursable}
                      onCheckedChange={(c) => setFormData({ ...formData, reimbursable: c })}
                    />
                    <Label htmlFor="reimbursable">Reimbursable</Label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit">{editingExpense ? 'Update' : 'Add'} Expense</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Billable Total</p>
                  <p className="text-xl font-bold">${summary.billable_total?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Receipt className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Non-Billable</p>
                  <p className="text-xl font-bold">${summary.non_billable_total?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-xl font-bold">
                    {summary.by_status?.find(s => s._id === 'pending')?.count || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Approved</p>
                  <p className="text-xl font-bold">
                    {summary.by_status?.find(s => s._id === 'approved')?.count || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Tabs */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search expenses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Tabs value={activeStatus} onValueChange={setActiveStatus}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="billed">Billed</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Expenses Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Date</th>
                  <th className="text-left p-4 font-medium text-slate-600">Description</th>
                  <th className="text-left p-4 font-medium text-slate-600">Category</th>
                  <th className="text-left p-4 font-medium text-slate-600">Amount</th>
                  <th className="text-left p-4 font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense) => (
                  <tr key={expense.expense_id} className="border-b hover:bg-slate-50">
                    <td className="p-4 text-sm">{formatDate(expense.date)}</td>
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{expense.description}</p>
                        {expense.vendor && <p className="text-sm text-slate-500">{expense.vendor}</p>}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">{getCategoryLabel(expense.category)}</Badge>
                    </td>
                    <td className="p-4 font-medium">${expense.amount.toFixed(2)}</td>
                    <td className="p-4">
                      <Badge className={getStatusColor(expense.status)}>{expense.status}</Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {expense.status === 'pending' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleStatusChange(expense.expense_id, 'billed')} 
                              className="text-green-600"
                              title="Add to Invoice"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(expense)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setDeleteId(expense.expense_id)} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredExpenses.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500">
                      No expenses found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the expense record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Expenses;
